<article class="doc">
  <header class="doc__header">
    <p class="doc__eyebrow">Atom</p>
    <h1 class="doc__title">Select</h1>
    <p class="doc__lead">
      Campo de selección única con búsqueda interna, badge obligatorio/opcional y botón limpiar.
      El panel se cierra al seleccionar una opción, al hacer click fuera o al presionar
      <code>Esc</code>. Borde normal <code>#dce0e8</code>, foco <code>#4169e2</code>.
    </p>
  </header>

  <!-- ── Estados ─────────────────────────────── -->
  <h2 class="doc-section-title">Estados</h2>

  <div class="doc-grid">
    <app-example-section
      title="Normal · Obligatorio"
      description="Estado por defecto con campo requerido."
    >
      <ui-select
        [block]="true"
        label="Ciudad"
        [options]="cities"
        [required]="true"
      />
    </app-example-section>

    <app-example-section
      title="Normal · Opcional"
      description="Muestra 'Opcional' a la derecha del label."
    >
      <ui-select
        [block]="true"
        label="Ciudad"
        [options]="cities"
        [required]="false"
        [showOptionalHint]="true"
      />
    </app-example-section>

    <app-example-section
      title="Con valor seleccionado"
      description="Muestra la opción activa y el botón limpiar."
    >
      <ui-select
        [block]="true"
        label="Ciudad"
        [options]="cities"
        [(selected)]="selectedCity"
        [required]="true"
      />
      @if (selectedCity()) {
        <p class="doc-value-preview">Seleccionado: {{ selectedCity()?.label }}</p>
      }
    </app-example-section>

    <app-example-section
      title="Error / inválido"
      description="Borde rojo. showMessage controla la visibilidad del texto de error."
    >
      <div class="doc-example-stack">
        <ui-select
          [block]="true"
          label="Tipo de propiedad"
          [options]="propertyTypes"
          [required]="true"
          [invalid]="isInvalid()"
          message="Debes seleccionar un tipo de propiedad."
          [showMessage]="showErrorMsg()"
        />
        <div class="doc-toggle-row">
          <button type="button" class="doc-native-toggle" (click)="toggleInvalid()">
            {{ isInvalid() ? 'Quitar error' : 'Activar error' }}
          </button>
          <button type="button" class="doc-native-toggle" (click)="toggleErrorMsg()">
            {{ showErrorMsg() ? 'Ocultar mensaje' : 'Mostrar mensaje' }}
          </button>
        </div>
      </div>
    </app-example-section>

    <app-example-section
      title="Deshabilitado"
      description="Sin interacción; fondo atenuado."
    >
      <ui-select
        [block]="true"
        label="Ciudad"
        [options]="cities"
        [disabled]="true"
      />
    </app-example-section>

    <app-example-section
      title="Opciones deshabilitadas"
      description="Opciones individuales marcadas como disabled no se pueden seleccionar."
    >
      <ui-select
        [block]="true"
        label="Tipo de propiedad"
        [options]="propertyTypes"
        [showOptionalHint]="false"
      />
    </app-example-section>
  </div>

  <!-- ── Variantes ──────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Variantes pre-configuradas</h2>
    <p class="doc-block__p">
      Átomos derivados de <code>ui-select</code> con opciones fijas y configuración compacta
      (<code>[showLabel]="false"</code>, <code>[showOptionalHint]="false"</code>).
      Listos para usar directamente en <code>ui-title-bar</code> sin configuración adicional.
    </p>

    <div class="doc-grid">
      <app-example-section
        title="ui-year-select"
        description="Selector de año. Opciones: 2024, 2025, 2026. Ancho fijo 120 px."
      >
        <ui-year-select [(selected)]="selectedYear" />
        @if (selectedYear()) {
          <p class="doc-value-preview">Seleccionado: {{ selectedYear()?.label }}</p>
        }
      </app-example-section>

      <app-example-section
        title="ui-month-select"
        description="Selector de mes. Opciones: Enero – Diciembre. Ancho fijo 160 px."
      >
        <ui-month-select [(selected)]="selectedMonth" />
        @if (selectedMonth()) {
          <p class="doc-value-preview">Seleccionado: {{ selectedMonth()?.label }}</p>
        }
      </app-example-section>
    </div>

    <p class="doc-block__p">Uso en <code>ui-title-bar</code> (modo <code>control-panel</code>):</p>
    <pre class="doc-block__pre" role="code"><code>selectedYear = signal&lt;UiSelectOption | null&gt;(null);
selectedMonth = signal&lt;UiSelectOption | null&gt;(null);

&lt;ui-title-bar mode="control-panel" title="Dashboard" ...&gt;
  &lt;ui-year-select  [(selected)]="selectedYear"  /&gt;
  &lt;ui-month-select [(selected)]="selectedMonth" /&gt;
&lt;/ui-title-bar&gt;</code></pre>
  </section>

  <!-- ── Ejemplos de uso ─────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Ejemplos de uso</h2>

    <p class="doc-block__p">Binding bidireccional con signal:</p>
    <pre class="doc-block__pre" role="code"><code>cities: UiSelectOption[] = [
  &#123; id: 1, label: 'Ciudad de México' &#125;,
  &#123; id: 2, label: 'Guadalajara' &#125;,
];
selected = signal&lt;UiSelectOption | null&gt;(null);

&lt;ui-select
  label="Ciudad"
  [required]="true"
  [options]="cities"
  [(selected)]="selected"
/&gt;</code></pre>

    <p class="doc-block__p">Reaccionando al cambio sin two-way binding:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-select
  label="Tipo"
  [options]="types"
  (selectedChange)="onTypeChange($event)"
/&gt;</code></pre>

    <p class="doc-block__p">Con estado inválido controlado externamente:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-select
  label="Ciudad"
  [required]="true"
  [options]="cities"
  [invalid]="form.controls.city.invalid &amp;&amp; form.controls.city.touched"
  message="Este campo es requerido."
  [showMessage]="showMessages()"
/&gt;</code></pre>

    <p class="doc-block__p">Placeholder personalizado:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-select
  label="Estado"
  placeholder="Elige un estado"
  [options]="states"
/&gt;</code></pre>
  </section>

  <!-- ── Inputs ──────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Inputs</h2>
    <table class="doc-table">
      <thead>
        <tr>
          <th>Nombre</th>
          <th>Tipo</th>
          <th>Default</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>label</code></td>
          <td><code>string</code></td>
          <td><code>''</code></td>
          <td>Etiqueta visible sobre el campo.</td>
        </tr>
        <tr>
          <td><code>options</code></td>
          <td><code>UiSelectOption[]</code></td>
          <td><code>[]</code></td>
          <td>Lista de opciones del dropdown.</td>
        </tr>
        <tr>
          <td><code>selected</code></td>
          <td><code>UiSelectOption | null</code> (model)</td>
          <td><code>null</code></td>
          <td>Opción activa. Soporta <code>[(selected)]</code>.</td>
        </tr>
        <tr>
          <td><code>placeholder</code></td>
          <td><code>string</code></td>
          <td><code>'Selecciona una opción'</code></td>
          <td>Texto cuando no hay selección.</td>
        </tr>
        <tr>
          <td><code>searchPlaceholder</code></td>
          <td><code>string</code></td>
          <td><code>'Buscar...'</code></td>
          <td>Placeholder del input de búsqueda en el panel.</td>
        </tr>
        <tr>
          <td><code>required</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Activa <code>aria-required</code> y badge "Obligatorio".</td>
        </tr>
        <tr>
          <td><code>showOptionalHint</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Muestra "Opcional" cuando no es requerido.</td>
        </tr>
        <tr>
          <td><code>requiredHint</code></td>
          <td><code>string</code></td>
          <td><code>'Obligatorio'</code></td>
          <td>Texto del badge de campo requerido.</td>
        </tr>
        <tr>
          <td><code>optionalHint</code></td>
          <td><code>string</code></td>
          <td><code>'Opcional'</code></td>
          <td>Texto del badge de campo opcional.</td>
        </tr>
        <tr>
          <td><code>disabled</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Deshabilita el selector completo.</td>
        </tr>
        <tr>
          <td><code>invalid</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Activa borde rojo y <code>aria-invalid</code>.</td>
        </tr>
        <tr>
          <td><code>message</code></td>
          <td><code>string | undefined</code></td>
          <td><code>undefined</code></td>
          <td>Texto del mensaje de error.</td>
        </tr>
        <tr>
          <td><code>showMessage</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Controla si el mensaje se renderiza.</td>
        </tr>
        <tr>
          <td><code>showLabel</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Oculta el bloque de label + badge cuando es <code>false</code>. Útil en contextos compactos como filtros en <code>ui-title-bar</code>.</td>
        </tr>
        <tr>
          <td><code>showSearch</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Muestra u oculta el input de búsqueda dentro del panel.</td>
        </tr>
        <tr>
          <td><code>allowClear</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Muestra el botón × para limpiar la selección activa.</td>
        </tr>
        <tr>
          <td><code>block</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Cambia el host a <code>display: block</code>.</td>
        </tr>
        <tr>
          <td><code>fieldId</code></td>
          <td><code>string | undefined</code></td>
          <td>Auto-generado</td>
          <td>ID del trigger para la asociación con el <code>&lt;label&gt;</code>.</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- ── Teclado / Accesibilidad ───────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Teclado y accesibilidad</h2>
    <table class="doc-table">
      <thead>
        <tr>
          <th>Tecla</th>
          <th>Comportamiento</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><kbd>↓</kbd> / <kbd>↑</kbd></td>
          <td>Abre el panel si está cerrado; navega entre opciones cuando está abierto.</td>
        </tr>
        <tr>
          <td><kbd>Enter</kbd></td>
          <td>Abre el panel si está cerrado; selecciona la opción resaltada si está abierto.</td>
        </tr>
        <tr>
          <td><kbd>Esc</kbd></td>
          <td>Cierra el panel y devuelve el foco al trigger.</td>
        </tr>
        <tr>
          <td><kbd>Tab</kbd></td>
          <td>Cierra el panel y mueve el foco al siguiente elemento de la página.</td>
        </tr>
      </tbody>
    </table>
    <p class="doc-block__p">
      El componente implementa el patrón ARIA <em>Listbox</em>:
      <code>aria-haspopup="listbox"</code>, <code>aria-expanded</code>, <code>aria-controls</code>,
      <code>aria-activedescendant</code> y <code>role="option"</code> en cada ítem.
      El input de búsqueda lleva <code>role="combobox"</code> y actualiza
      <code>aria-activedescendant</code> en tiempo real al navegar con flechas.
    </p>
  </section>

  <!-- ── UiSelectOption ──────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Interfaz UiSelectOption</h2>
    <table class="doc-table">
      <thead>
        <tr>
          <th>Propiedad</th>
          <th>Tipo</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>id</code></td>
          <td><code>string | number</code></td>
          <td>Identificador único. Se usa para comparar la selección actual.</td>
        </tr>
        <tr>
          <td><code>label</code></td>
          <td><code>string</code></td>
          <td>Texto visible en el trigger y en la lista.</td>
        </tr>
        <tr>
          <td><code>value</code></td>
          <td><code>string | number | null</code></td>
          <td>Valor de dominio opcional (p. ej. un UUID o clave de API).</td>
        </tr>
        <tr>
          <td><code>disabled</code></td>
          <td><code>boolean</code></td>
          <td>Marca la opción como no seleccionable.</td>
        </tr>
        <tr>
          <td><code>color</code></td>
          <td><code>UiTagColor</code></td>
          <td>Opcional. Muestra la opción como <code>ui-tag</code> outline.</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- ── Outputs ─────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Outputs</h2>
    <table class="doc-table">
      <thead>
        <tr>
          <th>Nombre</th>
          <th>Tipo</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>selectedChange</code></td>
          <td><code>UiSelectOption | null</code></td>
          <td>Emite la opción seleccionada o <code>null</code> al limpiar.</td>
        </tr>
        <tr>
          <td><code>opened</code></td>
          <td><code>void</code></td>
          <td>Emite cuando el panel se abre.</td>
        </tr>
        <tr>
          <td><code>closed</code></td>
          <td><code>void</code></td>
          <td>Emite cuando el panel se cierra.</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- ── Buenas prácticas ───────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Buenas prácticas</h2>
    <ul class="doc-list">
      <li>Usa <code>id</code> único y estable (no índice de array) para que la selección sobreviva cambios en la lista.</li>
      <li>Guarda en el modelo el objeto <code>UiSelectOption</code> completo; extrae <code>value</code> o <code>id</code> solo al enviar el formulario.</li>
      <li>Activa <code>[invalid]</code> solo tras blur o submit para no mostrar errores prematuros.</li>
      <li>Usa <code>[showMessage]="false"</code> en grids compactos donde el espacio vertical es limitado.</li>
      <li>Para listas largas (más de 50 ítems), considera paginar o cargar bajo demanda y conectar la búsqueda al backend en lugar de filtrar en cliente.</li>
      <li>Para selección múltiple, usa un átomo separado (multiselect) para mantener cada componente con responsabilidad única.</li>
    </ul>
  </section>
</article>
